import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  SafeAreaView,
  ActivityIndicator,
  StyleSheet,
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  Modal,
  FlatList,
  Platform,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ShimmerPlaceHolder from 'react-native-shimmer-placeholder';
import { LinearGradient } from 'expo-linear-gradient';

const PublicBooking = ({ route, navigation }) => {
  const collabID = route?.params?.CollabID || '';
  const brandNameFromRoute = route?.params?.BrandName || '';

  const API_BASE = 'https://collabbay.com/APIControllers';
  const IMAGE_BASE = 'https://collabbayadmin.com/listings/';
  const FALLBACK_BANNER =
    'https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1';
  const TOP_LOGO = 'https://collabbay.com/SeatStreamLogo.png';

  const SCREEN_WIDTH = Dimensions.get('window').width;
  const MAX_BANNER_WIDTH = 620;
  const BANNER_WIDTH = Math.min(SCREEN_WIDTH, MAX_BANNER_WIDTH) * 0.92;
  const BANNER_HEIGHT = 238;

  const bannerListRef = useRef(null);
  const scaleAnim = useRef(new Animated.Value(0)).current;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offerData, setOfferData] = useState(null);
  const [error, setError] = useState('');

  const [bannerImages, setBannerImages] = useState([FALLBACK_BANNER]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [bannerLoaded, setBannerLoaded] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [plusOnes, setPlusOnes] = useState('1');
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');

  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const parseBannerImages = (csv) => {
    if (!csv || !csv.trim()) return [FALLBACK_BANNER];

    const arr = csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((u) => (u.startsWith('http') ? u : IMAGE_BASE + u));

    return arr.length ? arr : [FALLBACK_BANNER];
  };

  const formValid = useMemo(() => {
    return (
      name.trim() !== '' &&
      email.trim() !== '' &&
      number.trim() !== '' &&
      plusOnes.trim() !== '' &&
      bookingDate.trim() !== '' &&
      bookingTime.trim() !== ''
    );
  }, [name, email, number, plusOnes, bookingDate, bookingTime]);

  useEffect(() => {
    if (!collabID) {
      setError('Missing CollabID.');
      setLoading(false);
      return;
    }

    fetchOffer();
  }, [collabID]);

  useEffect(() => {
    if (successModalVisible) {
      scaleAnim.setValue(0);
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 5,
        tension: 80,
        useNativeDriver: true,
      }).start();
    }
  }, [successModalVisible, scaleAnim]);

  useEffect(() => {
    if (bannerImages.length <= 1) return;

    const id = setInterval(() => {
      setBannerIndex((prev) => {
        const next = (prev + 1) % bannerImages.length;
        bannerListRef.current?.scrollToOffset({
          offset: next * BANNER_WIDTH,
          animated: true,
        });
        return next;
      });
    }, 2600);

    return () => clearInterval(id);
  }, [bannerImages.length, BANNER_WIDTH]);

  const fetchOffer = async () => {
    try {
      setLoading(true);
      setError('');
      setBannerLoaded(false);

      const response = await fetch(
        `${API_BASE}/GetVenueBookingOfferForPublic.php?CollabID=${encodeURIComponent(collabID)}`
      );

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Failed to fetch offer.');
      }

      setOfferData(json.data);

      const parsedImages = parseBannerImages(json?.data?.LogoImage || '');
      setBannerImages(parsedImages);
      setBannerIndex(0);
    } catch (err) {
      setError(err.message || 'Failed to load booking page.');
    } finally {
      setLoading(false);
    }
  };

  const onBannerScrollEnd = (e) => {
    const x = e?.nativeEvent?.contentOffset?.x || 0;
    const idx = Math.round(x / BANNER_WIDTH);
    if (!Number.isNaN(idx)) setBannerIndex(idx);
  };

  const scrollTo = (i) => {
    if (!bannerListRef.current || bannerImages.length === 0) return;
    const idx = ((i % bannerImages.length) + bannerImages.length) % bannerImages.length;
    bannerListRef.current.scrollToOffset({
      offset: idx * BANNER_WIDTH,
      animated: true,
    });
    setBannerIndex(idx);
  };

  const nextBanner = () => scrollTo(bannerIndex + 1);

  const handleSubmit = async () => {
    if (!formValid) {
      setErrorMessage('Please complete all fields before submitting.');
      setErrorModalVisible(true);
      return;
    }

    const partySize = parseInt(plusOnes, 10);

    if (isNaN(partySize) || partySize < 1) {
      setErrorMessage('Party size must be at least 1.');
      setErrorModalVisible(true);
      return;
    }

    const fullBooksDate = `${bookingDate.trim()} ${bookingTime.trim()}`;

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE}/InsertPublicBooking.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          CollabID: collabID,
          Name: name.trim(),
          Email: email.trim(),
          Number: number.trim(),
          PlusOnes: partySize,
          BooksDate: fullBooksDate,
        }),
      });

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.message || 'Failed to submit booking.');
      }

      setSuccessMessage('Your booking request has been sent successfully.');
      setSuccessModalVisible(true);

      setName('');
      setEmail('');
      setNumber('');
      setPlusOnes('1');
      setBookingDate('');
      setBookingTime('');
    } catch (err) {
      setErrorMessage(err.message || 'Something went wrong.');
      setErrorModalVisible(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderHero = () => {
    return (
      <View style={styles.bannerContainer}>
        <FlatList
          ref={bannerListRef}
          data={bannerImages}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item, index) => `${item}-${index}`}
          onMomentumScrollEnd={onBannerScrollEnd}
          getItemLayout={(_, index) => ({
            length: BANNER_WIDTH,
            offset: BANNER_WIDTH * index,
            index,
          })}
          style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT, alignSelf: 'center' }}
          renderItem={({ item }) => (
            <ShimmerPlaceHolder
              LinearGradient={LinearGradient}
              visible={bannerLoaded}
              shimmerColors={['#e0e0e0', '#f3f3f3', '#e0e0e0']}
              style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT, borderRadius: 26 }}
            >
              <Image
                source={{ uri: item }}
                style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT, borderRadius: 26 }}
                resizeMode="cover"
                onLoad={() => setBannerLoaded(true)}
                onError={() => setBannerLoaded(true)}
              />
            </ShimmerPlaceHolder>
          )}
        />

        <LinearGradient
          colors={['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.28)']}
          style={styles.bannerShade}
          pointerEvents="none"
        />

        {navigation?.canGoBack?.() ? (
          <View style={styles.backContainer}>
            <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
              <Ionicons name="arrow-back" size={22} color="black" />
            </TouchableOpacity>
          </View>
        ) : null}

        {bannerImages.length > 1 && (
          <>
            <TouchableOpacity style={styles.bannerArrowRight} onPress={nextBanner} activeOpacity={0.85}>
              <Ionicons name="chevron-forward" size={20} color="#000" />
            </TouchableOpacity>

            <View style={styles.dotsWrap}>
              {bannerImages.map((_, i) => (
                <TouchableOpacity key={i} onPress={() => scrollTo(i)} activeOpacity={0.85}>
                  <View style={[styles.dot, i === bannerIndex && styles.dotActive]} />
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Image source={{ uri: TOP_LOGO }} style={styles.topLogoLoading} resizeMode="contain" />
          <ActivityIndicator size="large" color="black" />
          <Text style={styles.loadingText}>Loading booking page...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !offerData) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Image source={{ uri: TOP_LOGO }} style={styles.topLogoLoading} resizeMode="contain" />
          <View style={styles.errorCard}>
            <Ionicons name="alert-circle-outline" size={42} color="black" />
            <Text style={styles.errorTitle}>Booking page unavailable</Text>
            <Text style={styles.errorText}>{error || 'Offer not found.'}</Text>
            {!!brandNameFromRoute && <Text style={styles.errorBrand}>{brandNameFromRoute}</Text>}

            <TouchableOpacity style={styles.retryButton} onPress={fetchOffer}>
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={{
          maxWidth: 640,
          width: '100%',
          alignSelf: 'center',
          flex: 1,
        }}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.topLogoWrap}>
          <Image source={{ uri: TOP_LOGO }} style={styles.topLogo} resizeMode="contain" />
        </View>

        {renderHero()}

        <View style={styles.infoSection}>
          <View style={styles.titleRow}>
            <Text style={styles.headerSectionTitle}>
              {offerData?.BrandName}
              <Text style={{ color: '#FDF05D' }}>.</Text>
            </Text>
            <Text style={styles.headerSubTitle}>Public booking invitation</Text>
          </View>

          {!!offerData?.Location && (
            <View style={styles.metaPill}>
              <Ionicons name="location-sharp" size={16} color="black" />
              <Text style={styles.metaPillText}>{offerData.Location}</Text>
            </View>
          )}

          <LinearGradient
            colors={[
              'rgba(253,240,93,0.55)',
              'rgba(255,255,255,0.85)',
              'rgba(236,244,255,0.92)',
            ]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.offerGlassCard}
          >
            <View style={styles.offerGlassTopRow}>
              <View style={styles.offerGlassBadge}>
                <Ionicons name="sparkles-outline" size={14} color="#111" />
                <Text style={styles.offerGlassBadgeText}>Featured offer</Text>
              </View>

              <View style={styles.offerGlassMiniBadge}>
                <Text style={styles.offerGlassMiniBadgeText}>SeatStream</Text>
              </View>
            </View>

            <Text style={styles.offerGlassTitle}>What’s included</Text>

            <Text style={styles.offerGlassText}>
              {offerData?.VenuePublicOffer || 'N/A'}
            </Text>

            <View style={styles.creatorChip}>
              <Ionicons name="people-outline" size={16} color="black" />
              <Text style={styles.creatorChipText}>Shared via creator link</Text>
            </View>
          </LinearGradient>
        </View>

        <View style={styles.formShell}>
          <Text style={styles.formTitle}>Enter your booking details</Text>
          <Text style={styles.formSubtitle}>
            Fill in the fields below so the venue can review and confirm your request.
          </Text>

          <View style={styles.inputCard}>
            <Text style={styles.inputLabel}>Full name</Text>
            <Text style={styles.inputHelper}>Enter the name for this booking.</Text>
            <TextInput
              style={styles.inputField}
              placeholder="e.g. Alex Morgan"
              placeholderTextColor="#8f8f8f"
              value={name}
              onChangeText={setName}
            />
          </View>

          <View style={styles.inputCard}>
            <Text style={styles.inputLabel}>Email address</Text>
            <Text style={styles.inputHelper}>The venue may use this to confirm your booking.</Text>
            <TextInput
              style={styles.inputField}
              placeholder="e.g. alex@email.com"
              placeholderTextColor="#8f8f8f"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
            />
          </View>

          <View style={styles.inputCard}>
            <Text style={styles.inputLabel}>Phone number</Text>
            <Text style={styles.inputHelper}>Best contact number for booking updates.</Text>
            <TextInput
              style={styles.inputField}
              placeholder="e.g. 07123456789"
              placeholderTextColor="#8f8f8f"
              value={number}
              onChangeText={setNumber}
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.rowInputs}>
            <View style={[styles.inputCard, styles.halfCard]}>
              <Text style={styles.inputLabel}>Party size</Text>
              <Text style={styles.inputHelper}>Total number of guests.</Text>
              <TextInput
                style={styles.inputField}
                placeholder="e.g. 2"
                placeholderTextColor="#8f8f8f"
                value={plusOnes}
                onChangeText={setPlusOnes}
                keyboardType="numeric"
              />
            </View>

            <View style={[styles.inputCard, styles.halfCard]}>
              <Text style={styles.inputLabel}>Booking date</Text>
              <Text style={styles.inputHelper}>Use format YYYY-MM-DD.</Text>
              <TextInput
                style={styles.inputField}
                placeholder="e.g. 2026-03-30"
                placeholderTextColor="#8f8f8f"
                value={bookingDate}
                onChangeText={setBookingDate}
              />
            </View>
          </View>

          <View style={styles.inputCard}>
            <Text style={styles.inputLabel}>Booking time</Text>
            <Text style={styles.inputHelper}>Use 24-hour format, e.g. 19:00.</Text>
            <TextInput
              style={styles.inputField}
              placeholder="e.g. 19:00"
              placeholderTextColor="#8f8f8f"
              value={bookingTime}
              onChangeText={setBookingTime}
            />
          </View>

          <TouchableOpacity
            style={[styles.submitButton, submitting && { opacity: 0.65 }]}
            onPress={handleSubmit}
            disabled={submitting}
            activeOpacity={0.9}
          >
            {submitting ? (
              <ActivityIndicator color="#000" />
            ) : (
              <>
                <Text style={styles.submitButtonText}>Send booking request</Text>
                <Text style={styles.submitButtonSubtext}>
                  The venue will review your request after submission
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      <Modal
        animationType="fade"
        transparent={true}
        visible={successModalVisible}
        onRequestClose={() => setSuccessModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <Animated.View style={[styles.successCard, { transform: [{ scale: scaleAnim }] }]}>
            <Ionicons name="checkmark-circle" size={72} color="#2ecc71" style={{ marginBottom: 14 }} />
            <Text style={styles.successTitle}>Booking request sent</Text>
            <Text style={styles.successSubtitle}>{successMessage}</Text>

            <TouchableOpacity
              style={styles.successButton}
              onPress={() => setSuccessModalVisible(false)}
            >
              <Text style={styles.successButtonText}>OK</Text>
            </TouchableOpacity>
          </Animated.View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent={true}
        visible={errorModalVisible}
        onRequestClose={() => setErrorModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Ionicons name="alert-circle-outline" size={48} color="red" />
            <Text style={styles.modalText}>{errorMessage}</Text>
            <TouchableOpacity
              style={styles.modalButton}
              onPress={() => setErrorModalVisible(false)}
            >
              <Text style={styles.modalButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

export default PublicBooking;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },

  contentContainer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 36,
  },

  topLogoWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
    marginBottom: 14,
  },

  topLogo: {
    width: 122,
    height: 34,
  },

  topLogoLoading: {
    width: 122,
    height: 34,
    marginBottom: 18,
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 20,
  },

  loadingText: {
    marginTop: 12,
    color: '#555',
    fontSize: 15,
  },

  bannerContainer: {
    position: 'relative',
    marginBottom: 18,
    marginTop: 4,
  },

  bannerShade: {
    position: 'absolute',
    left: (Dimensions.get('window').width - Math.min(Dimensions.get('window').width, 620) * 0.92) / 2,
    width: Math.min(Dimensions.get('window').width, 620) * 0.92,
    height: 238,
    borderRadius: 26,
  },

  backContainer: {
    position: 'absolute',
    top: 12,
    left: 14,
    zIndex: 3,
  },

  backButton: {
    backgroundColor: 'white',
    borderRadius: 30,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.18,
    shadowRadius: 2,
    elevation: 3,
  },

  bannerArrowRight: {
    position: 'absolute',
    top: '50%',
    right: 10,
    transform: [{ translateY: -18 }],
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderWidth: 1,
    borderColor: '#e8e8e8',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 2,
  },

  dotsWrap: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },

  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.5)',
    marginHorizontal: 3,
  },

  dotActive: {
    width: 14,
    borderRadius: 7,
    backgroundColor: '#fff',
  },

  infoSection: {
    marginBottom: 18,
  },

  titleRow: {
    marginBottom: 12,
  },

  headerSectionTitle: {
    fontSize: 29,
    fontWeight: 'bold',
    color: '#000',
    lineHeight: 33,
  },

  headerSubTitle: {
    fontSize: 13,
    color: '#666',
    marginTop: 5,
    fontWeight: '500',
  },

  metaPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: '#f3f3f3',
    borderColor: '#f3f3f3',
    borderWidth: 1,
    borderRadius: 17,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 14,
  },

  metaPillText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#111',
  },

  offerGlassCard: {
    borderRadius: 24,
    padding: 16,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.7)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 10,
    elevation: 3,
  },

  offerGlassTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },

  offerGlassBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.7)',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
  },

  offerGlassBadgeText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '700',
    color: '#111',
  },

  offerGlassMiniBadge: {
    backgroundColor: 'rgba(17,17,17,0.92)',
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
  },

  offerGlassMiniBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.3,
  },

  offerGlassTitle: {
    fontSize: 14,
    fontWeight: '800',
    color: '#333',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },

  offerGlassText: {
    fontSize: 18,
    color: '#111',
    lineHeight: 25,
    fontWeight: '700',
    marginBottom: 14,
  },

  creatorChip: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.85)',
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 50,
    borderWidth: 1,
    borderColor: '#e7e7e7',
  },

  creatorChipText: {
    marginLeft: 7,
    fontSize: 13,
    color: '#333',
    fontWeight: '600',
  },

  formShell: {
    backgroundColor: '#fafafa',
    borderRadius: 24,
    padding: 16,
    borderWidth: 1,
    borderColor: '#efefef',
  },

  formTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#111',
    marginBottom: 6,
  },

  formSubtitle: {
    fontSize: 14,
    lineHeight: 21,
    color: '#666',
    marginBottom: 18,
  },

  inputCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 11,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#ededed',
  },

  rowInputs: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  halfCard: {
    width: '48.4%',
  },

  inputLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#666',
    textTransform: 'uppercase',
    marginBottom: 3,
    letterSpacing: 0.4,
  },

  inputHelper: {
    fontSize: 12,
    color: '#8a8a8a',
    marginBottom: 7,
    lineHeight: 16,
  },

  inputField: {
    fontSize: 16,
    color: '#111',
    paddingVertical: Platform.OS === 'web' ? 9 : 8,
    outlineStyle: 'none',
  },

  submitButton: {
    backgroundColor: '#FDF05D',
    paddingVertical: 16,
    borderRadius: 20,
    borderColor: '#f3e94f',
    alignItems: 'center',
    marginTop: 8,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08,
    shadowRadius: 5,
    elevation: 2,
  },

  submitButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
  },

  submitButtonSubtext: {
    fontSize: 12,
    color: '#4f4f4f',
    marginTop: 4,
    textAlign: 'center',
  },

  errorCard: {
    width: '88%',
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 22,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 6,
  },

  errorTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#111',
    marginTop: 8,
    marginBottom: 8,
    textAlign: 'center',
  },

  errorText: {
    fontSize: 15,
    color: '#666',
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 10,
  },

  errorBrand: {
    fontSize: 14,
    color: '#999',
    marginBottom: 18,
  },

  retryButton: {
    backgroundColor: '#111',
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 10,
  },

  retryButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 15,
  },

  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },

  modalContent: {
    width: '85%',
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 14,
    alignItems: 'center',
  },

  modalText: {
    fontSize: 16,
    textAlign: 'center',
    color: '#333',
    marginTop: 12,
    marginBottom: 20,
    lineHeight: 22,
  },

  modalButton: {
    backgroundColor: '#FDF05D',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },

  modalButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
  },

  successCard: {
    width: '85%',
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 6,
  },

  successTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 8,
  },

  successSubtitle: {
    fontSize: 14,
    color: '#555',
    textAlign: 'center',
    marginTop: 6,
    marginBottom: 18,
    lineHeight: 20,
  },

  successButton: {
    backgroundColor: '#FDF05D',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
  },

  successButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
  },
});